import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

export interface ExternalPartOption {
    internalId: string;
    partNo: string;
}

interface PartAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<ExternalPartOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

interface PartSearchResponse {
    data?: {
        items: ExternalPartOption[];
    }
}

const ExternalPartAutocomplete = <M extends boolean = false, >({
                                                                   textFieldProps = {},
                                                                   form = false,
                                                                   name = undefined,
                                                                   required = false,
                                                                   setLoadingOptions: setLoadingOptionsProps,
                                                                   ...props
                                                               }: PartAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches part options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (matches against part numbers)
     * @returns Array of matching part options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            const response = await Api.externalPartAutoFill({
                keyword: keywords,
                pagination: {pageSize: 20, currentPage: 1},
            }, cancelToken as any) as PartSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.items ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two part options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: ExternalPartOption, value: ExternalPartOption | null) =>
        option.internalId === value?.internalId, []);

    /**
     * Gets the display label for a part option (part number)
     */
    const getOptionLabel = useCallback((option: ExternalPartOption) => option?.partNo ?? "", []);

    /**
     * Gets the unique key for a part option
     */
    const getOptionKey = useCallback((option: ExternalPartOption) => option?.internalId ?? option?.partNo ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<ExternalPartOption, M>
            keywordsBased
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default ExternalPartAutocomplete;